import React from 'react';
import { Box, Typography, keyframes } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { BORDER_COLORS } from '../../utils/colors';
import { HERO_BUTTON } from '../../utils/responsive';

const glowPulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 15px rgba(220, 38, 38, 0.15), 0 5px 20px rgba(0, 0, 0, 0.3);
  }
  50% {
    box-shadow: 0 0 30px rgba(220, 38, 38, 0.3), 0 5px 20px rgba(0, 0, 0, 0.3);
  }
`;

const fadeInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const siegeFeatureKeys = ['outposts', 'cities', 'tactics'];
const mapFeatureKeys = ['territories', 'battles', 'events', 'navigation'];
const serverFeatureKeys = ['balance', 'economy', 'progress', 'goal', 'clans', 'openDev'];

const MapSection: React.FC = () => {
  const { t } = useTranslation('home');
  const navigate = useNavigate();
  const { ref: leftRef, isVisible: leftVisible } = useScrollAnimation(0.1);
  const { ref: rightRef, isVisible: rightVisible } = useScrollAnimation(0.1);
  const { ref: mapRef, isVisible: mapVisible } = useScrollAnimation(0.1);

  return (
    <Box
      sx={{
        position: 'relative',
        py: { xs: 6, md: 10 },
        px: { xs: 2, lg: 10 },
        minHeight: { xs: '100dvh', lg: '120dvh' },
      }}
    >
      {/* Background */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0,
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src="/img/map-bg_upscayl_2x.webp"
          alt=""
          sx={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '100vw',
            height: 'auto',
            minHeight: '100%',
            objectFit: 'cover',
            objectPosition: 'center top',
            opacity: 0.6,
          }}
        />
        {/* Top Gradient - fade from black */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '150px',
            background: 'linear-gradient(to bottom, #0a0a0a 0%, transparent 100%)',
            zIndex: 2,
            pointerEvents: 'none',
          }}
        />
        {/* Bottom Gradient - fade to black */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '150px',
            background: 'linear-gradient(to top, #0a0a0a 0%, transparent 100%)',
            zIndex: 2,
            pointerEvents: 'none',
          }}
        />
        {/* Left Gradient */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            width: '15%',
            background: 'linear-gradient(to right, #0a0a0a 0%, transparent 100%)',
            zIndex: 2,
            pointerEvents: 'none',
          }}
        />
        {/* Right Gradient */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            right: 0,
            width: '15%',
            background: 'linear-gradient(to left, #0a0a0a 0%, transparent 100%)',
            zIndex: 2,
            pointerEvents: 'none',
          }}
        />
      </Box>

      <Box sx={{ position: 'relative', zIndex: 1 }}>
        {/* Three Column Layout */}
        <Box
          sx={{
            display: 'grid',
            // Плавная адаптация: колонки растут от 350px до 630px в зависимости от ширины экрана
            gridTemplateColumns: { xs: '1fr', lg: 'clamp(350px, 28vw, 630px) 1fr clamp(350px, 28vw, 630px)' },
            gap: { xs: 4, md: 0 },
          }}
        >
          {/* Left Column - Siege Features */}
          <Box
            ref={leftRef}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              height: '100%',
              opacity: 0,
              animation: leftVisible ? `${fadeInLeft} 0.8s ease-out both` : 'none',
            }}
          >
            {/* Top part - Title and Siege Cards */}
            <Box>
              {/* Left Title */}
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 800,
                    color: '#fff',
                    textTransform: 'uppercase',
                    fontSize: { xs: '1.4rem', md: '2.2rem' },
                    mb: 2,
                    lineHeight: 1.2,
                  }}
                >
                  {t('map.siegeTitle')}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: 'text.secondary', fontSize: '1.1rem', lineHeight: 1.6 }}
                >
                  {t('map.siegeSubtitle')}
                </Typography>
              </Box>

              {/* Siege Cards */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {siegeFeatureKeys.map((key) => (
                  <Box
                    key={key}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 2,
                      p: 2.5,
                      background: 'rgba(0, 0, 0, 0.6)',
                      backdropFilter: 'blur(10px)',
                      borderRadius: 3,
                      border: `1px solid ${BORDER_COLORS.DEFAULT}`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: 'rgba(0, 0, 0, 0.8)',
                        borderColor: 'rgba(185, 28, 28, 0.6)',
                        transform: 'translateX(10px)',
                        boxShadow: '0 0 20px rgba(185, 28, 28, 0.35)',
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src="/img/gorisontal-card-1.webp"
                      alt=""
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        flexShrink: 0,
                        transition: 'transform 0.3s ease',
                      }}
                    />
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'text.secondary',
                        lineHeight: 1.6,
                        fontSize: '0.95rem',
                      }}
                    >
                      {t(`map.siegeFeatures.${key}`)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Map Section inside left column */}
            <Box
              ref={mapRef}
              sx={{
                mt: 'auto',
                pt: 40,
                // Плавная адаптация: карта растёт от 700px до 1400px в зависимости от ширины экрана
                width: { lg: 'clamp(700px, 62vw, 1400px)' },
                opacity: 0,
                animation: mapVisible ? `${fadeInUp} 0.8s ease-out both` : 'none',
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  mb: { xs: 2, lg: 4 },
                  textTransform: 'uppercase',
                  fontSize: { xs: '1.6rem', md: '2.5rem' },
                  color: '#fff',
                }}
              >
                {t('map.mapTitle')}
              </Typography>

              {/* Map Features - Mobile Only */}
              <Box
                sx={{
                  display: { xs: 'flex', lg: 'none' },
                  flexDirection: 'column',
                  gap: 1.5,
                  mb: 3,
                }}
              >
                {mapFeatureKeys.map((key) => (
                  <Box
                    key={key}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                    }}
                  >
                    <Box
                      component="img"
                      src="/img/gorisontal-card-1.webp"
                      alt=""
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1,
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        fontSize: '0.9rem',
                        lineHeight: 1.5,
                      }}
                    >
                      {t(`map.mapFeatures.${key}`)}
                    </Typography>
                  </Box>
                ))}
                {/* View Map Button - Mobile */}
                <Box
                  component="button"
                  onClick={() => navigate('/map')}
                  sx={{
                    mt: 1,
                    alignSelf: 'flex-start',
                    height: HERO_BUTTON.height,
                    px: HERO_BUTTON.px,
                    background: 'rgba(0, 0, 0, 0.5)',
                    backdropFilter: 'blur(12px)',
                    border: `1px solid ${BORDER_COLORS.DEFAULT}`,
                    borderRadius: HERO_BUTTON.borderRadius,
                    color: '#fff',
                    fontWeight: 500,
                    fontSize: HERO_BUTTON.fontSize,
                    letterSpacing: HERO_BUTTON.letterSpacing,
                    textTransform: 'uppercase',
                    cursor: 'pointer',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    animation: `${glowPulse} 2.5s ease-in-out infinite`,
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: '20%',
                      right: '20%',
                      height: '2px',
                      background: '#dc2626',
                      borderRadius: '2px',
                      boxShadow: '0 0 10px rgba(220, 38, 38, 0.6)',
                      transition: 'all 0.4s ease',
                    },
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      background: 'rgba(220, 38, 38, 0.1)',
                      borderColor: 'rgba(220, 38, 38, 0.3)',
                      boxShadow: '0 20px 50px rgba(0, 0, 0, 0.4), 0 0 30px rgba(220, 38, 38, 0.15)',
                      animationPlayState: 'paused',
                      '&::after': {
                        left: '5%',
                        right: '5%',
                        boxShadow: '0 0 25px rgba(220, 38, 38, 0.8)',
                      },
                    },
                    '&:active': {
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  {t('map.viewMap')}
                </Box>
              </Box>

              {/* Map Card with features inside */}
              <Box
                sx={{
                  position: 'relative',
                  borderRadius: 3,
                  overflow: 'hidden',
                  border: `1px solid ${BORDER_COLORS.LIGHTER}`,
                  boxShadow: '0 0 20px rgba(185, 28, 28, 0.2)',
                  transition: 'all 0.4s ease',
                  '&:hover': {
                    border: '1px solid rgba(185, 28, 28, 0.6)',
                    boxShadow: '0 0 60px rgba(185, 28, 28, 0.5)',
                    transform: 'translateY(-5px)',
                    '& .map-image': {
                      transform: 'scale(1.05)',
                    },
                  },
                }}
              >
                <Box
                  className="map-image"
                  component="img"
                  src="/img/home/map-picture.webp"
                  alt="Map"
                  sx={{
                    width: '100%',
                    display: 'block',
                    transition: 'transform 0.6s ease',
                  }}
                />
                {/* Overlay with features - Desktop Only */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    p: 'clamp(16px, 2vw, 32px)',
                    display: { xs: 'none', lg: 'flex' },
                    flexDirection: 'column',
                    justifyContent: 'center',
                    gap: 'clamp(16px, 1.5vw, 32px)',
                    background: 'linear-gradient(to right, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 40%, transparent 70%)',
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: { lg: 1.5, xl: 2, '@media (min-width: 1800px)': 3 } }}>
                    {mapFeatureKeys.map((key) => (
                      <Box
                        key={key}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: { lg: 1, xl: 1.5, '@media (min-width: 1800px)': 2 },
                        }}
                      >
                        <Box
                          component="img"
                          src="/img/gorisontal-card-1.webp"
                          alt=""
                          sx={{
                            width: 'clamp(32px, 2.2vw, 48px)',
                            height: 'clamp(32px, 2.2vw, 48px)',
                            borderRadius: 1
                          }}
                        />
                        <Typography variant="body1" sx={{
                          color: '#fff',
                          fontSize: 'clamp(0.8rem, 0.9vw, 1.1rem)',
                          lineHeight: 1.5
                        }}>
                          {t(`map.mapFeatures.${key}`)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                  {/* View Map Button */}
                  <Box
                    component="button"
                    onClick={() => navigate('/map')}
                    sx={{
                      alignSelf: 'flex-start',
                      px: 'clamp(24px, 2vw, 40px)',
                      py: 'clamp(12px, 1vw, 16px)',
                      background: 'rgba(0, 0, 0, 0.5)',
                      backdropFilter: 'blur(12px)',
                      border: `1px solid ${BORDER_COLORS.DEFAULT}`,
                      borderRadius: '10px',
                      color: '#fff',
                      fontWeight: 500,
                      fontSize: 'clamp(0.75rem, 0.8vw, 0.95rem)',
                      letterSpacing: '2px',
                      textTransform: 'uppercase',
                      cursor: 'pointer',
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'all 0.4s ease',
                      animation: `${glowPulse} 2.5s ease-in-out infinite`,
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: '20%',
                        right: '20%',
                        height: '2px',
                        background: '#dc2626',
                        borderRadius: '2px',
                        boxShadow: '0 0 10px rgba(220, 38, 38, 0.6)',
                        transition: 'all 0.4s ease',
                      },
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        background: 'rgba(220, 38, 38, 0.15)',
                        borderColor: 'rgba(220, 38, 38, 0.4)',
                        boxShadow: '0 15px 40px rgba(0, 0, 0, 0.4), 0 0 20px rgba(220, 38, 38, 0.2)',
                        animationPlayState: 'paused',
                        '&::after': {
                          left: '5%',
                          right: '5%',
                          boxShadow: '0 0 20px rgba(220, 38, 38, 0.8)',
                        },
                      },
                    }}
                  >
                    {t('map.viewMap')}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Center Column - Empty for character visibility */}
          <Box sx={{ display: { xs: 'none', lg: 'block' } }} />

          {/* Right Column - Server Features */}
          <Box
            ref={rightRef}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              height: '100%',
              opacity: 0,
              animation: rightVisible ? `${fadeInRight} 0.8s ease-out both` : 'none',
            }}
          >
            {/* Right Title */}
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                color: '#fff',
                textTransform: 'uppercase',
                fontSize: { xs: '1.6rem', md: '2.5rem' },
                textAlign: { xs: 'left', lg: 'right' },
                fontStyle: 'italic',
                lineHeight: 1.2,
                mb: 4,
              }}
            >
              {t('map.serverTitle')}
            </Typography>

            {/* Server Features */}
            {serverFeatureKeys.map((key) => (
              <Box key={key} sx={{ textAlign: { xs: 'left', lg: 'right' }, mb: 4 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                    color: '#fff',
                    fontSize: { xs: '1.1rem', md: '1.5rem' },
                    textTransform: 'uppercase',
                  }}
                >
                  {t(`map.serverFeatures.${key}.title`)}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'text.secondary',
                    fontSize: { xs: '1rem', md: '1.1rem' },
                    lineHeight: 1.7,
                  }}
                >
                  {t(`map.serverFeatures.${key}.text`)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MapSection;
